import isEmpty from 'lodash/isEmpty';
import every from 'lodash/every';
import some from 'lodash/some';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import { createSelector } from 'reselect';
import Breadcrumb from '@Services/Breadcrumb';
import { generateAbsoluteIndexesInRange } from '@Containers/PatentFamilyClustering/utils';
import { REDUCER_KEY_RESULTS } from './constants';
import { mapResultItemToChecked, mapAllResultsToChecked } from './utils';

export const resultsState = (state) => state.get(REDUCER_KEY_RESULTS);
export const resultsPerPage = (state) => resultsState(state).resultsPerPage;
export const resultsFinished = (state) => resultsState(state).resultsFinished;
export const paramsFromProps = (state, ownProps) => ownProps.params;
export const resultsItems = (state) => resultsState(state).resultsItems;

export const paginationVisible = createSelector(
  [resultsItems, resultsFinished],
  (_resultsItems, _resultsFinished) => _resultsFinished && !isEmpty(_resultsItems)
);

/* Selection */
export const checkedItems = (state) => resultsState(state).checkedItems;
export const checkedResults = (state) => checkedItems(state).results;
export const checkedProducts = (state) => checkedItems(state).products;

/* Toolbar checkbox */
export const isAllResultsChecked = createSelector([resultsItems, checkedResults], (_resultsItems, _checkedResults) => {
  const mappedResultsToChecked = mapAllResultsToChecked(_resultsItems);

  return (
    !isEmpty(mappedResultsToChecked) &&
    every(mappedResultsToChecked, ({ index: resultIndex, absoluteIndex: resultAbsoluteIndex }) => {
      if (resultAbsoluteIndex) {
        // Patent Family Clustering
        return _checkedResults.find(({ absoluteIndex: checkedResultAbsoluteIndex }) => checkedResultAbsoluteIndex === resultAbsoluteIndex);
      }

      // Generic results
      return _checkedResults.find(({ index: checkedResultIndex }) => checkedResultIndex === resultIndex);
    })
  );
});

export const isAllResultsCheckedPartial = createSelector(
  [resultsItems, checkedResults, checkedProducts, isAllResultsChecked],
  (_resultsItems, _checkedResults, _checkedProducts, _isAllResultsChecked) => {
    const mappedResultsToChecked = mapAllResultsToChecked(_resultsItems);

    return (
      !_isAllResultsChecked &&
      some(mappedResultsToChecked, ({ index: resultIndex, absoluteIndex: resultAbsoluteIndex }) => {
        // Patent Family Clustering
        if (resultAbsoluteIndex) {
          return _checkedResults.find(
            ({ absoluteIndex: checkedResultAbsoluteIndex }) => checkedResultAbsoluteIndex === resultAbsoluteIndex
          );
        }

        // Generic results
        return (
          _checkedResults.find(({ index: checkedResultIndex }) => checkedResultIndex === resultIndex) ||
          _checkedProducts.find(({ substanceIndex: checkedSubstanceIndex }) => checkedSubstanceIndex === resultIndex)
        );
      })
    );
  }
);

/* Result checkbox */
export const isResultChecked = (state, { index, absoluteIndex }) => {
  const _checkedResults = checkedResults(state);

  // Generic results
  if (!absoluteIndex) {
    return !!_checkedResults.find(({ index: checkedResultIndex }) => checkedResultIndex === index);
  }

  // Patent Family Clustering - main result item
  if (isArray(absoluteIndex)) {
    return every(
      generateAbsoluteIndexesInRange(absoluteIndex),
      (absoluteIndexFromRange) =>
        !!_checkedResults.find(({ absoluteIndex: checkedResultAbsoluteIndex }) => checkedResultAbsoluteIndex === absoluteIndexFromRange)
    );
  }

  // Patent Family Clustering - single patents or family members
  return !!_checkedResults.find(({ absoluteIndex: checkedResultAbsoluteIndex }) => checkedResultAbsoluteIndex === absoluteIndex);
};

export const isResultCheckedPartial = (state, { index, absoluteIndex }) => {
  const _checkedResults = checkedResults(state);

  // Generic results
  if (!absoluteIndex) {
    const _checkedProducts = checkedProducts(state);
    const checkedResultItem = _checkedResults.find(({ index: checkedResultIndex }) => checkedResultIndex === index);
    const checkedProductsForResultItem = _checkedProducts.find(({ substanceIndex }) => substanceIndex === index);

    return !checkedResultItem && !isEmpty(checkedProductsForResultItem);
  }

  // Patent Family Clustering - main result item
  if (isArray(absoluteIndex)) {
    const resultChecked = isResultChecked(state, { index, absoluteIndex });
    const checkedInRange = !!generateAbsoluteIndexesInRange(absoluteIndex).find(
      (absoluteIndexFromRange) =>
        !!_checkedResults.find(({ absoluteIndex: checkedResultAbsoluteIndex }) => checkedResultAbsoluteIndex === absoluteIndexFromRange)
    );

    return !resultChecked && checkedInRange;
  }

  // Patent Family Clustering - single patents or family members
  return false;
};

/* Product checkbox */
export const isProductChecked = (state, { substanceIndex, productIndex }) => {
  const _checkedProducts = checkedProducts(state);

  return (
    isResultChecked(state, { index: substanceIndex }) ||
    !!_checkedProducts.find(
      ({ substanceIndex: checkedSubstanceIndex, productIndex: checkedProductIndex }) =>
        checkedSubstanceIndex === substanceIndex && checkedProductIndex === productIndex
    )
  );
};

export const breadcrumbCheckedItemsNumber = (state, { databaseId }) => {
  const breadcrumbCheckedItems = Breadcrumb.getResultsStateForDb(databaseId, 'checkedItems');
  const results = get(breadcrumbCheckedItems, 'results', []);
  return results.length;
};

export const normalizedCheckedItems = createSelector([checkedResults, checkedProducts], (_checkedResults, _checkedProducts) => {
  let mapped = {};

  mapped = _checkedResults.reduce((memo, checkedResult) => {
    const index = get(checkedResult, 'index');
    const absoluteIndex = get(checkedResult, 'absoluteIndex');

    // Patent Family Clustering
    if (absoluteIndex) {
      return {
        ...memo,
        [absoluteIndex]: checkedResult,
      };
    }

    // Generic results
    return {
      ...memo,
      [index]: checkedResult,
    };
  }, mapped);

  mapped = _checkedProducts.reduce((memo, checkedProduct) => {
    const substanceId = get(checkedProduct, 'substanceId');
    const substanceIndex = get(checkedProduct, 'substanceIndex');
    const substance = memo[substanceIndex] || mapResultItemToChecked({ id: substanceId, index: substanceIndex });
    const products = substance.products || [];

    return {
      ...memo,
      [substanceIndex]: {
        ...substance,
        products: products.concat(checkedProduct),
      },
    };
  }, mapped);

  return Object.values(mapped);
});

export const checkedItemsNumber = createSelector(
  [checkedResults, normalizedCheckedItems],
  (_checkedResults, _normalizedCheckedItems) => _normalizedCheckedItems.length
);

export const isLimitDisabled = (state) => !checkedItemsNumber(state);
